import React, { useState, useEffect } from 'react';
import { SocialLoginButtonsMaybe } from '../../containers/AuthenticationPage/AuthenticationPage';
import css from './LoginPopupModal.module.css';
import { NamedLink } from '../../components';

const CandidateIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const AgencyIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    <line x1="12" y1="12" x2="12" y2="16" />
    <line x1="10" y1="14" x2="14" y2="14" />
  </svg>
);

const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

/**
 * A lightweight modal that shows SSO login options.
 * Reuses SocialLoginButtonsMaybe from AuthenticationPage for consistent auth logic.
 *
 * @component
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {string} [props.userType] - Preselected user type (e.g. 'candidate'). Ignored when showRoleSelect is true.
 * @param {string} [props.from] - The URL to return to after authentication
 * @param {boolean} [props.showRoleSelect] - Show role selection step before sign-in buttons
 */
const LoginPopupModal = ({ isOpen, onClose, userType = 'candidate', from, applyWorkId, showRoleSelect = false, agencyOnly = false }) => {
  const [selectedRole, setSelectedRole] = useState(null);

  useEffect(() => {
    if (!isOpen) setSelectedRole(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setSelectedRole(null);
    onClose();
  };

  const showRoleStep = showRoleSelect && !selectedRole && !agencyOnly;
  const effectiveUserType = agencyOnly ? 'agency' : (selectedRole || userType);

  return (
    <div className={css.backdrop} onClick={handleClose} role="dialog" aria-modal="true" aria-label="Sign in">
      <div className={css.modal} onClick={e => e.stopPropagation()}>

        <button className={css.closeBtn} onClick={handleClose} aria-label="Close">
          &times;
        </button>

        {showRoleStep ? (
          <>
            <div className={css.header}>
              <h2 className={css.title}>How would you like to apply?</h2>
              <p className={css.subtitle}>
                Choose your role to create or sign in to your account.
              </p>
            </div>

            <div className={css.roleButtons}>
              <button
                className={css.roleBtn}
                onClick={() => setSelectedRole('candidate')}
              >
                <span className={css.roleIcon}><CandidateIcon /></span>
                <span className={css.roleText}>
                  <span className={css.roleLabel}>Apply as Candidate</span>
                  <span className={css.roleDesc}>Looking for a job opportunity</span>
                </span>
              </button>

              <button
                className={css.roleBtn}
                onClick={() => setSelectedRole('agency')}
              >
                <span className={css.roleIcon}><AgencyIcon /></span>
                <span className={css.roleText}>
                  <span className={css.roleLabel}>Apply as Supplier</span>
                  <span className={css.roleDesc}>Apply to jobs and contracts on behalf of candidates</span>
                </span>
              </button>
            </div>
          </>
        ) : (
          <>
            {showRoleSelect && !agencyOnly && (
              <button className={css.backBtn} onClick={() => setSelectedRole(null)} aria-label="Back">
                <BackIcon /> Back
              </button>
            )}

            <div className={css.header}>
              <h2 className={css.title}>Sign in to continue</h2>
              <p className={css.subtitle}>
                Create a free account or sign in to apply for this opportunity.
              </p>
            </div>

            <SocialLoginButtonsMaybe
              isLogin={true}
              showFacebookLogin={!!process.env.REACT_APP_FACEBOOK_APP_ID}
              showGoogleLogin={!!process.env.REACT_APP_GOOGLE_CLIENT_ID}
              showLinkedInLogin={true}
              showMicrosoftLogin={true}
              userType={effectiveUserType}
              from={from}
              applyWorkId={applyWorkId}
            />

            <p className={css.privacyPolicyNote}>
              By signing up, you agree to our{' '}
              <NamedLink className={css.privacyPolicyLink} name="TermsAndConditionsCustomPage">
                Terms and Conditions
              </NamedLink>
              {' '}and{' '}
              <NamedLink className={css.privacyPolicyLink} name="PrivacyPolicyCustomPage">
                Privacy Policy
              </NamedLink>
            </p>
          </>
        )}

      </div>
    </div>
  );
};

export default LoginPopupModal;
